"use client";

import { useLanguage } from "@/app/context/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import Image from "next/image";
import {
  Moon,
  Sun,
  Sword,
  Flower2,
  Utensils,
  Heart,
  PartyPopper,
  Star,
  Calendar,
  Sparkles,
  Phone,
  MapPin,
  ArrowRight,
  Flame,
  Wand2,
  Lamp,
  ChevronRight,
  Check,
  Zap,
  AlarmClock,
} from "lucide-react";

const content = {
  en: {
    badge: "Sacred Celebrations",
    title: "Events & Festivals",
    subtitle: "Join us in celebrating the divine grace of Karuppanasamy",
    filterAll: "All Events",
    filters: ["All Events", "Upcoming", "Aadi", "Annadhanam", "Functions"],
    contactCta: "For event details call",
    upcomingTitle: "Upcoming Festivals",
    upcomingSubtitle: "Mark your calendar for these sacred occasions",
    featuredLabel: "Featured",
    specialLabel: "Special",
    monthlyLabel: "Monthly",
    weeklyLabel: "Weekly",
    annualLabel: "Annual",
    registerCta: "Get Directions",
    
    noEvents: "No events in this category. Check back soon!",
    events: [
      {
        id: 1,
        category: "Upcoming",
        tag: "Monthly",
        tagColor: "amber",
        icon: <Moon className="h-5 w-5" />,
        title: "Amavasai Grand Celebration",
        tamilTitle: "அமாவாசை பெரு விழா",
        date: "Monthly (New Moon Day)",
        dateShort: "Monthly",
        month: "Every Month",
        image: "/events/ammavasai.png",
        desc: "A grand full-day celebration on every new moon day. Special ancestral prayers, abhishekam, and Dheepaaradhana performed with hundreds of oil lamps.",
        featured: true,
      },
      {
        id: 2,
        category: "Upcoming",
        tag: "Monthly",
        tagColor: "yellow",
        icon: <Sun className="h-5 w-5" />,
        title: "Pavarnamni Full Moon Festival",
        tamilTitle: "பவுர்ணமி விழா",
        date: "Monthly (Full Moon Day)",
        dateShort: "Monthly",
        month: "Every Month",
        image: "/events/Pavarnamni Full Moon Festival.png",
        desc: "Full moon day brings a grand celebration at the temple. The deity is adorned with special decorations and a lamp procession lights up the night.",
        featured: false,
      },
      {
        id: 3,
        category: "Aadi",
        tag: "Annual",
        tagColor: "orange",
        icon: <Sword className="h-5 w-5" />,
        title: "Aadi Karuppanasamy Festival",
        tamilTitle: "ஆடி கருப்பண்ணசாமி விழா",
        date: "Aadi Month (Jul–Aug)",
        dateShort: "Aadi",
        month: "July – August",
        image: "/events/Aadi Karuppanasamy Festival1.png",
        desc: "The most auspicious month of Aadi is celebrated with grand rituals, special abhishekam to Karuppanasamy, and devotees offering special vows and prayers.",
        featured: true,
      },
      {
        id: 4,
        category: "Aadi",
        tag: "Annual",
        tagColor: "red",
        icon: <Flower2 className="h-5 w-5" />,
        title: "Aadi Kaali Amman Pooja",
        tamilTitle: "ஆடி காளி அம்மன் பூஜை",
        date: "Aadi Month (Jul–Aug)",
        dateShort: "Aadi",
        month: "July – August",
        image: "/events/Aadi Kaali Amman Pooja.png",
        desc: "Kaali Amman receives grand worship during the Aadi month. Special flower garlands, kumkum archana, and fire rituals are performed.",
        featured: false,
      },
      {
        id: 5,
        category: "Annadhanam",
        tag: "Special",
        tagColor: "green",
        icon: <Utensils className="h-5 w-5" />,
        title: "Annadhanam -  Sacred Food Offering",
        tamilTitle: "அன்னதானம்",
        date: "Festival Days & Special Occasions",
        dateShort: "Festival Days",
        month: "On Special Occasions",
        image: "/events/Annadhanam - Sacred Food Offering.png",
        desc: "Annadhanam is performed on major festival days where free meals are offered to all devotees. A tradition of love, service, and divine blessing.",
        featured: true,
      },
      {
        id: 6,
        category: "Annadhanam",
        tag: "Monthly",
        tagColor: "green",
        icon: <Heart className="h-5 w-5" />,
        title: "Amavasai Annadhanam",
        tamilTitle: "அமாவாசை அன்னதானம்",
        date: "Monthly (Amavasai Day)",
        dateShort: "Monthly",
        month: "Every Amavasai",
        image: "/events/Amavasai Annadhanam.png",
        desc: "On every Amavasai day, free meals are distributed to all visiting devotees as a form of charity and devotion to the Lord.",
        featured: false,
      },
      {
        id: 7,
        category: "Functions",
        tag: "Annual",
        tagColor: "amber",
        icon: <PartyPopper className="h-5 w-5" />,
        title: "Temple Anniversary - 12 Years",
        tamilTitle: "கோவில் ஆண்டு விழா -  12 ஆண்டுகள்",
        date: "2026 (Since 2014)",
        dateShort: "Annual",
        month: "Annual Celebration",
        image: "/events/Temple Anniversary - 12 Years.png",
        desc: "The 18m Padi Karuppanasamy Temple was inaugurated in 2014 and has completed 12 glorious years. Annual anniversary celebrations include grand poojas, processions, and community feasts.",
        featured: true,
      },
      {
        id: 8,
        category: "Functions",
        tag: "Weekly",
        tagColor: "amber",
        icon: <Star className="h-5 w-5" />,
        title: "Tuesday & Friday Special Pooja",
        tamilTitle: "செவ்வாய் & வெள்ளி சிறப்பு பூஜை",
        date: "Every Tuesday & Friday",
        dateShort: "Weekly",
        month: "Every Week",
        image: "/events/Tuesday & Friday Special Pooja.png",
        desc: "Every Tuesday and Friday, the temple remains open the full day with special poojas, flower decorations, and extended darshan for all devotees.",
        featured: false,
      },
      {
        id: 9,
        category: "Upcoming",
        tag: "Monthly",
        tagColor: "orange",
        icon: <Moon className="h-5 w-5" />,
        title: "Ashtami & Navami Special Pooja",
        tamilTitle: "அஷ்டமி & நவமி சிறப்பு பூஜை",
        date: "8th & 9th Lunar Day",
        dateShort: "Monthly",
        month: "Every Month",
        image: "/events/Ashtami & Navami Special Pooja.png",
        desc: "On the 8th and 9th lunar days each month, a special evening pooja is conducted from 4:30 PM to 6:00 PM with elaborate rituals.",
        featured: false,
      },
    ],
  },

  ta: {
    badge: "புனித கொண்டாட்டங்கள்",
    title: "நிகழ்வுகள் & விழாக்கள்",
    subtitle: "கருப்பண்ணசாமியின் அருளை கொண்டாட எங்களுடன் இணையுங்கள்",
    filterAll: "அனைத்தும்",
    filters: [
      "அனைத்தும்",
      "வரவிருக்கும்",
      "ஆடி",
      "அன்னதானம்",
      "சிறப்பு நிகழ்வுகள்",
    ],
    contactCta: "நிகழ்வு விவரங்களுக்கு அழைக்கவும்",
    upcomingTitle: "வரவிருக்கும் விழாக்கள்",
    upcomingSubtitle: "இந்த புனித நாட்களுக்கு தயாராகுங்கள்",
    featuredLabel: "சிறப்பு",
    specialLabel: "சிறப்பு",
    monthlyLabel: "மாதாந்திரம்",
    weeklyLabel: "வாராந்திரம்",
    annualLabel: "ஆண்டுதோறும்",
    registerCta: "வழி காட்டு",
    
    noEvents: "இந்த பிரிவில் நிகழ்வுகள் இல்லை. விரைவில் சரிபார்க்கவும்!",
    events: [
      {
        id: 1,
        category: "வரவிருக்கும்",
        tag: "மாதாந்திரம்",
        tagColor: "amber",
        icon: <Moon className="h-5 w-5" />,
        title: "அமாவாசை பெரு விழா",
        tamilTitle: "Amavasai Grand Celebration",
        date: "மாதாந்திரம் (அமாவாசை நாள்)",
        dateShort: "மாதாந்திரம்",
        month: "ஒவ்வொரு மாதமும்",
        image: "/events/ammavasai.png",
        desc: "ஒவ்வொரு அமாவாசை நாளும் முழு நாள் கொண்டாட்டம். சிறப்பு முன்னோர் வழிபாடு, அபிஷேகம் மற்றும் நூற்றுக்கணக்கான எண்ணெய் தீபங்களுடன் தீபாராதனை.",
        featured: true,
      },
      {
        id: 2,
        category: "வரவிருக்கும்",
        tag: "மாதாந்திரம்",
        tagColor: "yellow",
        icon: <Sun className="h-5 w-5" />,
        title: "பவுர்ணமி விழா",
        tamilTitle: "Pavarnamni Full Moon Festival",
        date: "மாதாந்திரம் (பவுர்ணமி நாள்)",
        dateShort: "மாதாந்திரம்",
        month: "ஒவ்வொரு மாதமும்",
        image: "/events/Pavarnamni Full Moon Festival.png",
        desc: "பவுர்ணமி நாளில் கோவிலில் பெரும் கொண்டாட்டம். தெய்வம் சிறப்பு அலங்காரத்துடன் அலங்கரிக்கப்பட்டு இரவு தீப ஊர்வலம் நடைபெறும்.",
        featured: false,
      },
      {
        id: 3,
        category: "ஆடி",
        tag: "ஆண்டுதோறும்",
        tagColor: "orange",
        icon: <Sword className="h-5 w-5" />,
        title: "ஆடி கருப்பண்ணசாமி விழா",
        tamilTitle: "Aadi Karuppanasamy Festival",
        date: "ஆடி மாதம் (ஜூலை–ஆகஸ்ட்)",
        dateShort: "ஆடி",
        month: "ஜூலை – ஆகஸ்ட்",
        image: "/events/Aadi Karuppanasamy Festival1.png",
        desc: "மிகவும் சுபமான ஆடி மாதம் பெரும் சடங்குகளுடன் கொண்டாடப்படுகிறது. கருப்பண்ணசாமிக்கு சிறப்பு அபிஷேகம் மற்றும் பக்தர்கள் சிறப்பு நேர்த்திக்கடன் நிறைவேற்றுவர்.",
        featured: true,
      },
      {
        id: 4,
        category: "ஆடி",
        tag: "ஆண்டுதோறும்",
        tagColor: "red",
        icon: <Flower2 className="h-5 w-5" />,
        title: "ஆடி காளி அம்மன் பூஜை",
        tamilTitle: "Aadi Kaali Amman Pooja",
        date: "ஆடி மாதம் (ஜூலை–ஆகஸ்ட்)",
        dateShort: "ஆடி",
        month: "ஜூலை – ஆகஸ்ட்",
        image: "/events/Aadi Kaali Amman Pooja.png",
        desc: "ஆடி மாதத்தில் காளி அம்மனுக்கு பெரும் வழிபாடு நடைபெறும். சிறப்பு மலர் மாலை, குங்கும அர்ச்சனை மற்றும் தீ சடங்குகள் செய்யப்படும்.",
        featured: false,
      },
      {
        id: 5,
        category: "அன்னதானம்",
        tag: "சிறப்பு",
        tagColor: "green",
        icon: <Utensils className="h-5 w-5" />,
        title: "அன்னதானம் -  புனித உணவு வழங்கல்",
        tamilTitle: "Annadhanam",
        date: "விழா நாட்கள் & சிறப்பு தருணங்கள்",
        dateShort: "விழா நாட்கள்",
        month: "சிறப்பு தருணங்களில்",
        image: "/events/Annadhanam - Sacred Food Offering.png",
        desc: "பெரிய விழா நாட்களில் அனைத்து பக்தர்களுக்கும் இலவச உணவு வழங்கப்படுகிறது. அன்பு, சேவை மற்றும் தெய்வீக ஆசீர்வாதத்தின் மரபு.",
        featured: true,
      },
      {
        id: 6,
        category: "அன்னதானம்",
        tag: "மாதாந்திரம்",
        tagColor: "green",
        icon: <Heart className="h-5 w-5" />,
        title: "அமாவாசை அன்னதானம்",
        tamilTitle: "Amavasai Annadhanam",
        date: "மாதாந்திரம் (அமாவாசை நாள்)",
        dateShort: "மாதாந்திரம்",
        month: "ஒவ்வொரு அமாவாசையும்",
        image: "/events/Amavasai Annadhanam.png",
        desc: "ஒவ்வொரு அமாவாசை நாளும் வருகை தரும் அனைத்து பக்தர்களுக்கும் இலவச உணவு வழங்கப்படுகிறது.",
        featured: false,
      },
      {
        id: 7,
        category: "சிறப்பு நிகழ்வுகள்",
        tag: "ஆண்டுதோறும்",
        tagColor: "amber",
        icon: <PartyPopper className="h-5 w-5" />,
        title: "கோவில் ஆண்டு விழா -  12 ஆண்டுகள்",
        tamilTitle: "Temple Anniversary -  12 Years",
        date: "2026 (2014 முதல்)",
        dateShort: "ஆண்டு விழா",
        month: "ஆண்டு கொண்டாட்டம்",
        image: "/events/Temple Anniversary - 12 Years.png",
        desc: "18ம் படி கருப்பண்ணசாமி கோவில் 2014 இல் திறக்கப்பட்டு 12 பெருமை மிக்க ஆண்டுகளை நிறைவு செய்துள்ளது.",
        featured: true,
      },
      {
        id: 8,
        category: "சிறப்பு நிகழ்வுகள்",
        tag: "வாராந்திரம்",
        tagColor: "amber",
        icon: <Star className="h-5 w-5" />,
        title: "செவ்வாய் & வெள்ளி சிறப்பு பூஜை",
        tamilTitle: "Tuesday & Friday Special Pooja",
        date: "ஒவ்வொரு செவ்வாய் & வெள்ளி",
        dateShort: "வாராந்திரம்",
        month: "ஒவ்வொரு வாரமும்",
        image: "/events/Tuesday & Friday Special Pooja.png",
        desc: "ஒவ்வொரு செவ்வாய் மற்றும் வெள்ளியன்று கோவில் முழு நாளும் திறந்திருக்கும். சிறப்பு பூஜைகள், மலர் அலங்காரம் மற்றும் நீட்டிக்கப்பட்ட தரிசனம்.",
        featured: false,
      },
      {
        id: 9,
        category: "வரவிருக்கும்",
        tag: "மாதாந்திரம்",
        tagColor: "orange",
        icon: <Moon className="h-5 w-5" />,
        title: "அஷ்டமி & நவமி சிறப்பு பூஜை",
        tamilTitle: "Ashtami & Navami Special Pooja",
        date: "8ம் & 9ம் சந்திர நாள்",
        dateShort: "மாதாந்திரம்",
        month: "ஒவ்வொரு மாதமும்",
        image: "/events/Ashtami & Navami Special Pooja.png",
        desc: "ஒவ்வொரு மாதமும் 8ம் மற்றும் 9ம் சந்திர நாட்களில் மாலை 4:30 மணி முதல் 6:00 மணி வரை விரிவான சடங்குகளுடன் சிறப்பு மாலை பூஜை நடைபெறும்.",
        featured: false,
      },
    ],
  },
};

const tagColorMap: Record<string, string> = {
  amber: "bg-amber-950/60 text-amber-300 border-amber-700/50",
  orange: "bg-orange-950/60 text-orange-300 border-orange-700/50",
  yellow: "bg-yellow-950/60 text-yellow-300 border-yellow-700/50",
  red: "bg-red-950/60 text-red-300 border-red-700/50",
  green: "bg-green-950/60 text-green-300 border-green-700/50",
};

const filterIconMap: React.ReactNode[] = [
  <Sparkles className="h-3.5 w-3.5" />,
  <Calendar className="h-3.5 w-3.5" />,
  <Sword className="h-3.5 w-3.5" />,
  <Utensils className="h-3.5 w-3.5" />,
  <Star className="h-3.5 w-3.5" />,
];

export default function EventsPage() {
  const { language } = useLanguage();
  const t = content[language as "en" | "ta"] ?? content.en;
  const [activeFilter, setActiveFilter] = useState(0);

  const filterKeys =
    language === "ta"
      ? ["அனைத்தும்", "வரவிருக்கும்", "ஆடி", "அன்னதானம்", "சிறப்பு நிகழ்வுகள்"]
      : ["All Events", "Upcoming", "Aadi", "Annadhanam", "Functions"];

  const categoryMap =
    language === "ta"
      ? ["", "வரவிருக்கும்", "ஆடி", "அன்னதானம்", "சிறப்பு நிகழ்வுகள்"]
      : ["", "Upcoming", "Aadi", "Annadhanam", "Functions"];

  const filtered =
    activeFilter === 0
      ? t.events
      : t.events.filter((e) => e.category === categoryMap[activeFilter]);

  const featured = t.events.filter((e) => e.featured);

  return (
    <main className="bg-[#080604] text-neutral-100 min-h-screen selection:bg-amber-900 selection:text-amber-100 overflow-x-hidden">
      {/* ── HERO ── */}
      <section className="relative min-h-[75vh] flex flex-col items-center justify-center text-center px-4 overflow-hidden">
        {/* Multi-layer atmospheric backdrop */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/35 via-[#080604]/80 to-[#080604]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-transparent" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[700px] bg-amber-700/8 rounded-full blur-[160px] pointer-events-none" />
        <div className="absolute top-16 left-8 w-72 h-72 bg-orange-900/12 rounded-full blur-[90px] pointer-events-none" />
        <div className="absolute bottom-16 right-8 w-96 h-96 bg-amber-800/8 rounded-full blur-[110px] pointer-events-none" />
        {/* Sacred dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.022]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #f59e0b 1px, transparent 1px)",
            backgroundSize: "38px 38px",
          }}
        />

        <div className="relative z-10 space-y-7 max-w-4xl mx-auto pt-24 pb-16">
          {/* Ornament */}
          <div className="flex items-center justify-center gap-3">
            <div className="h-px w-14 bg-gradient-to-r from-transparent to-amber-600/60" />
            <Flame className="h-4 w-4 text-amber-500" />
            <Sparkles className="h-3 w-3 text-amber-400" />
            <Flame className="h-4 w-4 text-amber-500" />
            <div className="h-px w-14 bg-gradient-to-l from-transparent to-amber-600/60" />
          </div>

          <Badge
            variant="outline"
            className="text-amber-300 border-amber-500/40 bg-amber-950/40 px-6 py-2 text-xs font-medium tracking-[0.3em] uppercase rounded-full"
          >
            {t.badge}
          </Badge>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold leading-none tracking-tight">
            <span className="bg-gradient-to-br from-amber-100 via-yellow-300 to-orange-500 bg-clip-text text-transparent">
              {t.title}
            </span>
          </h1>

          <p className="text-base md:text-xl text-amber-100/40 max-w-2xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>

          {/* Stats row */}
          <div className="flex flex-wrap justify-center gap-1 pt-2">
            {[
              {
                val: "2014",
                label: language === "ta" ? "திறப்பு ஆண்டு" : "Est. Year",
              },
              { val: "12+", label: language === "ta" ? "ஆண்டுகள்" : "Years" },
              { val: "22ft", label: language === "ta" ? "சிலை" : "Statue" },
              {
                val: "6+",
                label: language === "ta" ? "தெய்வங்கள்" : "Deities",
              },
            ].map((s, i) => (
              <div
                key={i}
                className="flex flex-col items-center px-8 py-4 rounded-2xl border border-neutral-800/60 bg-neutral-900/30"
              >
                <p className="text-2xl font-bold text-amber-400 leading-tight">
                  {s.val}
                </p>
                <p className="text-[10px] text-neutral-600 uppercase tracking-[0.2em] mt-0.5">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#080604] to-transparent" />
      </section>

      {/* ── FEATURED SPOTLIGHT ── */}
      <section className="py-24 border-t border-amber-900/20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-950/10 to-transparent pointer-events-none" />

        <div className="container mx-auto px-5 lg:px-16 max-w-7xl">
          {/* Section header */}
          <div className="text-center mb-14 space-y-3">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-600/50" />
              <Star className="h-4 w-4 text-amber-500 fill-amber-500/30" />
              <span className="text-amber-600 text-xs tracking-[0.25em] uppercase font-medium">
                {t.upcomingTitle}
              </span>
              <Star className="h-4 w-4 text-amber-500 fill-amber-500/30" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-600/50" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {t.upcomingTitle}
            </h2>
            <p className="text-neutral-500 text-sm">{t.upcomingSubtitle}</p>
          </div>

          {/* Hero 2-col featured cards */}
          <div className="grid lg:grid-cols-2 gap-6">
            {featured.slice(0, 2).map((event, idx) => (
              <div
                key={event.id}
                className="group relative rounded-3xl overflow-hidden border border-neutral-800 hover:border-amber-700/50 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-950/40 min-h-[380px] flex flex-col justify-end"
              >
                {/* Full bleed image */}
                <div className="absolute inset-0">
                  <Image
                    src={event.image}
                    alt={event.title}
                    fill
                    className="object-cover opacity-35 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#080604] via-[#080604]/75 to-transparent" />
                </div>

                {/* Top accent bar */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-600/0 via-amber-500/60 to-amber-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Top-right corner bracket */}
                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-amber-600/30 rounded-tr-lg" />
                <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-amber-600/30 rounded-tl-lg" />

                {/* Content */}
                <div className="relative z-10 p-8 space-y-4">
                  {/* Badges row */}
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      <span
                        className={`text-xs border px-2.5 py-1 rounded-full font-semibold ${tagColorMap[event.tagColor] || tagColorMap.amber}`}
                      >
                        {event.tag}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs border px-2.5 py-1 rounded-full font-semibold bg-amber-600/20 text-amber-400 border-amber-600/30">
                        <Star className="h-3 w-3 fill-amber-400/40" />{" "}
                        {t.featuredLabel}
                      </span>
                    </div>
                    <div className="w-10 h-10 rounded-2xl bg-amber-950/60 border border-amber-800/40 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
                      {event.icon}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-2xl font-bold text-white group-hover:text-amber-200 transition-colors leading-tight mb-1">
                      {event.title}
                    </h3>
                    <p className="text-xs text-neutral-600 italic">
                      {event.tamilTitle}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 text-amber-500/80 text-xs">
                    <Calendar className="h-3.5 w-3.5" />
                    <span className="uppercase tracking-widest">
                      {event.date}
                    </span>
                  </div>

                  <p className="text-neutral-400 text-sm leading-relaxed line-clamp-2">
                    {event.desc}
                  </p>

                  <a
                    href="https://www.google.com/maps/search/Sukkampatti+Karur+Tamil+Nadu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-700/20 hover:bg-amber-700/40 text-amber-300 border border-amber-700/40 text-sm font-semibold transition-all hover:gap-3"
                  >
                    {t.registerCta}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Secondary featured row -  3 smaller */}
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            {featured.slice(2, 5).map((event) => (
              <div
                key={event.id}
                className="group relative rounded-2xl overflow-hidden border border-neutral-800 hover:border-amber-800/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-amber-950/30 min-h-[200px] flex flex-col justify-end"
              >
                <div className="absolute inset-0">
                  <Image
                    src={event.image}
                    alt={event.title}
                    fill
                    className="object-cover opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#080604]/95 via-[#080604]/60 to-transparent" />
                </div>
                <div className="relative z-10 p-5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-[10px] border px-2 py-0.5 rounded-full font-semibold ${tagColorMap[event.tagColor] || tagColorMap.amber}`}
                    >
                      {event.tag}
                    </span>
                    <div className="text-amber-500">{event.icon}</div>
                  </div>
                  <h4 className="text-sm font-bold text-white group-hover:text-amber-200 transition-colors leading-snug">
                    {event.title}
                  </h4>
                  <p className="text-neutral-600 text-xs flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {event.dateShort}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FILTER + GRID ── */}
      <section className="py-20 border-t border-amber-900/20">
        <div className="container mx-auto px-5 lg:px-16 max-w-7xl">
          {/* Filter pills */}
          <div className="flex gap-2 overflow-x-auto mb-14 pb-1 justify-start md:justify-center">
            {filterKeys.map((label, i) => (
              <button
                key={i}
                onClick={() => setActiveFilter(i)}
                className={`shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 border
                  ${
                    activeFilter === i
                      ? "bg-amber-600 text-white border-amber-500 shadow-lg shadow-amber-900/40 scale-105"
                      : "bg-neutral-900/60 text-neutral-400 border-neutral-800 hover:border-amber-800/50 hover:text-amber-400"
                  }`}
              >
                <span
                  className={
                    activeFilter === i ? "text-white" : "text-amber-600"
                  }
                >
                  {filterIconMap[i]}
                </span>
                {label}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-24 space-y-4">
              <div className="w-16 h-16 rounded-full border border-neutral-800 flex items-center justify-center mx-auto text-neutral-600">
                <Sparkles className="h-7 w-7" />
              </div>
              <p className="text-neutral-600 text-sm">{t.noEvents}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((event, idx) => (
                <div
                  key={event.id}
                  className="group flex flex-col rounded-3xl overflow-hidden border border-neutral-800 bg-neutral-900/30 hover:border-amber-800/40 hover:bg-neutral-900/60 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-amber-950/30"
                >
                  {/* Image area */}
                  <div className="relative h-52 overflow-hidden">
                    <Image
                      src={event.image}
                      alt={event.title}
                      fill
                      className="object-cover opacity-55 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/40 to-transparent" />

                    {/* Top badges */}
                    <div className="absolute top-3 left-3 flex gap-1.5">
                      <span
                        className={`text-[10px] border px-2 py-0.5 rounded-full font-semibold backdrop-blur-sm ${tagColorMap[event.tagColor] || tagColorMap.amber}`}
                      >
                        {event.tag}
                      </span>
                      {event.featured && (
                        <span className="inline-flex items-center text-[10px] border px-2 py-0.5 rounded-full font-semibold backdrop-blur-sm bg-amber-600/30 text-amber-400 border-amber-600/40">
                          <Star className="h-2.5 w-2.5 fill-amber-400/50" />
                        </span>
                      )}
                    </div>

                    {/* Icon chip bottom-right */}
                    <div className="absolute bottom-3 right-3 w-10 h-10 rounded-xl bg-[#080604]/80 backdrop-blur-sm border border-neutral-700/60 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
                      {event.icon}
                    </div>

                    {/* Number watermark */}
                    <span className="absolute bottom-3 left-3 text-4xl font-black text-neutral-700/50 group-hover:text-amber-900/30 transition-colors leading-none select-none">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>

                  {/* Card body */}
                  <div className="flex flex-col flex-1 p-6 space-y-3">
                    <div className="flex items-center gap-2 text-amber-600/70 text-xs">
                      <Calendar className="h-3 w-3 shrink-0" />
                      <span className="uppercase tracking-widest">
                        {event.dateShort} · {event.month}
                      </span>
                    </div>

                    <h3 className="text-base font-bold text-neutral-100 group-hover:text-amber-200 transition-colors leading-snug">
                      {event.title}
                    </h3>
                    <p className="text-[11px] text-neutral-700 italic leading-snug">
                      {event.tamilTitle}
                    </p>

                    <div className="h-px bg-neutral-800/80 group-hover:bg-amber-900/30 transition-colors" />

                    <p className="text-neutral-500 text-sm leading-relaxed flex-1 line-clamp-3">
                      {event.desc}
                    </p>

                    {/* Footer row */}
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-[10px] text-neutral-600 bg-neutral-800/60 border border-neutral-700/60 px-2.5 py-1 rounded-full">
                        {event.category}
                      </span>
                      <a
                        href="https://www.google.com/maps/search/Sukkampatti+Karur+Tamil+Nadu"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-amber-500 hover:text-amber-300 text-xs font-semibold transition-all group/link"
                      >
                      
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── AADI HIGHLIGHT BANNER ── */}
      <section className="py-20 border-t border-amber-900/20 relative overflow-hidden">
        {/* Warm background wash */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-950/25 via-amber-950/15 to-[#080604]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-700/40 to-transparent" />
        <div className="absolute top-8 left-1/4 w-80 h-80 bg-orange-800/12 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-8 right-1/4 w-64 h-64 bg-amber-700/10 rounded-full blur-[80px] pointer-events-none" />

        <div className="relative z-10 container mx-auto px-5 lg:px-16 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left text */}
            <div className="space-y-7 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-px w-8 bg-orange-700/50" />
                <span className="text-orange-500 text-xs tracking-[0.25em] uppercase font-medium">
                  {language === "ta" ? "ஆண்டு விழா" : "Annual Festival"}
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight break-words hyphens-auto">
                {language === "ta"
                  ? "ஆடி மாதம் -  கருப்பண்ணசாமியின் மாதம்"
                  : "Aadi Month -  The Month of Karuppanasamy"}
              </h2>
              <p className="text-neutral-400 leading-relaxed text-[15px] break-words">
                {language === "ta"
                  ? "ஆடி மாதம் (ஜூலை-ஆகஸ்ட்) 18ம் படி கருப்பண்ணசாமி கோவிலில் மிகவும் சிறப்பாக கொண்டாடப்படுகிறது. சிறப்பு அபிஷேகம், நேர்த்திக்கடன் நிறைவேற்றல், அன்னதானம் மற்றும் பெரும் ஊர்வலம் நடைபெறும்."
                  : "The Aadi month (July–August) is the most auspicious period at 18m Padi Karuppanasamy Temple. Special abhishekam, vow fulfillment ceremonies, annadhanam, and grand processions mark this sacred time."}
              </p>

              <div className="grid grid-cols-2 gap-3">
                {(language === "ta"
                  ? [
                      "சிறப்பு அபிஷேகம்",
                      "நேர்த்திக்கடன்",
                      "அன்னதானம்",
                      "பெரும் ஊர்வலம்",
                    ]
                  : [
                      "Special Abhishekam",
                      "Vow Ceremonies",
                      "Annadhanam",
                      "Grand Procession",
                    ]
                ).map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3.5 rounded-2xl border border-amber-900/30 bg-amber-950/20"
                  >
                    <div className="w-6 h-6 rounded-full bg-amber-600/20 border border-amber-700/40 flex items-center justify-center shrink-0">
                      <Check className="h-3.5 w-3.5 text-amber-400" />
                    </div>
                    <span className="text-amber-200/80 text-sm font-medium">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right image stack */}
            <div className="relative w-full h-[420px] min-w-0">
              <div className="absolute inset-0 rounded-3xl overflow-hidden border border-orange-900/40">
                <Image
                  src="/events/Aadi Karuppanasamy Festival.png"
                  alt="Aadi Festival"
                  fill
                  className="object-cover opacity-45"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#080604]/90 via-[#080604]/30 to-transparent" />
                {/* Frame corners */}
                <div className="absolute top-4 left-4 w-10 h-10 border-t-2 border-l-2 border-orange-600/50 rounded-tl-lg" />
                <div className="absolute top-4 right-4 w-10 h-10 border-t-2 border-r-2 border-orange-600/50 rounded-tr-lg" />
                <div className="absolute bottom-4 left-4 w-10 h-10 border-b-2 border-l-2 border-orange-600/50 rounded-bl-lg" />
                <div className="absolute bottom-4 right-4 w-10 h-10 border-b-2 border-r-2 border-orange-600/50 rounded-br-lg" />
                <div className="absolute bottom-6 left-6">
                  <div className="inline-flex items-center gap-2 bg-orange-950/70 border border-orange-800/40 px-4 py-2 rounded-xl backdrop-blur-sm">
                    <Sword className="h-4 w-4 text-orange-400" />
                    <span className="text-orange-300 font-semibold text-sm">
                      {language === "ta" ? "ஆடி விழா" : "Aadi Festival"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ANNADHANAM FEATURE ── */}
      <section className="py-20 border-t border-amber-900/20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-950/8 to-transparent pointer-events-none" />

        <div className="container mx-auto px-5 lg:px-16 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            {/* Image */}
            <div className="relative h-[400px] rounded-3xl overflow-hidden border border-green-900/30 order-2 lg:order-1">
              <Image
                src="/events/Divine Service.jpg"
                alt="Annadhanam"
                fill
                className="object-cover opacity-50"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080604]/85 to-transparent" />
              {/* Frame corners */}
              <div className="absolute top-4 left-4 w-10 h-10 border-t-2 border-l-2 border-green-600/40 rounded-tl-lg" />
              <div className="absolute bottom-4 right-4 w-10 h-10 border-b-2 border-r-2 border-green-600/40 rounded-br-lg" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="inline-flex items-center gap-2 bg-green-950/70 border border-green-800/40 px-4 py-2.5 rounded-xl backdrop-blur-sm">
                  <Utensils className="h-4 w-4 text-green-400" />
                  <div>
                    <p className="text-green-300 font-bold text-sm">
                      {language === "ta" ? "அன்னதானம்" : "Annadhanam"}
                    </p>
                    <p className="text-green-700 text-xs">
                      {language === "ta" ? "தெய்வீக சேவை" : "Divine Service"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="space-y-7 order-1 lg:order-2">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-px w-8 bg-green-700/50" />
                  <span className="text-green-500 text-xs tracking-[0.25em] uppercase font-medium flex items-center gap-1">
                    <Utensils className="h-3 w-3" />
                    {language === "ta" ? "தெய்வீக சேவை" : "Divine Service"}
                  </span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                  {language === "ta"
                    ? "உணவே உயிர், சேவையே பூஜை"
                    : "Food is Life, Service is Worship"}
                </h2>
                <div className="flex items-center gap-2 mt-4">
                  <div className="h-1 w-12 bg-green-600 rounded-full" />
                  <div className="h-1 w-4 bg-green-800 rounded-full" />
                </div>
              </div>

              <p className="text-neutral-400 leading-relaxed text-[15px]">
                {language === "ta"
                  ? "அமாவாசை மற்றும் பெரிய விழா நாட்களில் 18ம் படி கருப்பண்ணசாமி கோவிலில் இலவச உணவு வழங்கப்படுகிறது. இது கருப்பண்ணசாமியின் அருளால் நடைபெறும் தெய்வீக சேவை."
                  : "On Amavasai and major festival days, free meals are lovingly prepared and distributed to all devotees at 18m Padi Karuppanasamy Temple. A divine act of service blessed by Karuppanasamy."}
              </p>

              <div className="grid grid-cols-2 gap-4">
                {[
                  {
                    icon: <Moon className="h-5 w-5" />,
                    label: language === "ta" ? "அமாவாசை" : "Amavasai",
                    sub: language === "ta" ? "ஒவ்வொரு மாதமும்" : "Every Month",
                    color: "border-amber-900/40 bg-amber-950/20 text-amber-400",
                  },
                  {
                    icon: <PartyPopper className="h-5 w-5" />,
                    label: language === "ta" ? "விழா நாட்கள்" : "Festival Days",
                    sub:
                      language === "ta"
                        ? "சிறப்பு நேரங்கள்"
                        : "Special Occasions",
                    color: "border-green-900/40 bg-green-950/20 text-green-400",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 p-5 rounded-2xl border ${item.color} transition-all hover:-translate-y-0.5`}
                  >
                    <div className="shrink-0">{item.icon}</div>
                    <div>
                      <p className="text-neutral-200 font-bold text-sm">
                        {item.label}
                      </p>
                      <p className="text-neutral-600 text-xs mt-0.5">
                        {item.sub}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACT FOOTER ── */}
      <section className="py-16 border-t border-amber-900/20 bg-gradient-to-t from-amber-950/10 to-transparent">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center space-y-7">
            <div className="flex items-center justify-center gap-3">
              <div className="h-px w-10 bg-amber-900/40" />
              <Heart className="h-4 w-4 text-amber-700 fill-amber-700/30" />
              <span className="text-neutral-500 text-sm">{t.contactCta}</span>
              <Heart className="h-4 w-4 text-amber-700 fill-amber-700/30" />
              <div className="h-px w-10 bg-amber-900/40" />
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              {["9597255401", "9361986095"].map((num, i) => (
                <a key={i} href={`tel:${num}`} className="group">
                  <div className="flex items-center gap-3 px-6 py-4 rounded-2xl border border-neutral-800 bg-neutral-900/60 hover:border-amber-700/50 hover:bg-amber-950/25 transition-all duration-300 hover:-translate-y-0.5">
                    <div className="w-9 h-9 rounded-xl bg-amber-950/60 border border-amber-900/40 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
                      <Phone className="h-4 w-4" />
                    </div>
                    <span className="text-amber-300 font-mono tracking-wider font-semibold">
                      +91 {num}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-neutral-600 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </a>
              ))}
            </div>

            <div className="flex items-center justify-center gap-2 text-neutral-700 text-xs">
              <MapPin className="h-3 w-3 shrink-0" />
              <span>
                {language === "ta"
                  ? "கடவூர் தாலுகா, கணியாலம்பட்டி ரோடு, சுக்கம்பட்டி, கரூர்"
                  : "Kadavoor Thaluk, Kaniyalampatti Road, Sukkampatti, Karur"}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Minimal footer line */}
      <div className="border-t border-amber-900/20 py-5 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="h-px w-8 bg-amber-900/30" />
          <Flame className="h-3 w-3 text-amber-800" />
          <p className="text-neutral-800 text-xs tracking-[0.3em] uppercase">
            18m Padi Karuppanasamy Temple
          </p>
          <Flame className="h-3 w-3 text-amber-800" />
          <div className="h-px w-8 bg-amber-900/30" />
        </div>
      </div>
    </main>
  );
}
